import React, { useState, useEffect, memo } from "react";
import { compose } from "redux";
import { useSelector, useDispatch, connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import forEach from "lodash/forEach";
import Alert from "react-s-alert";

import Spinner, { styles } from "components/Spinner";

import {
  forms,
  paths,
  CIRCLES_PAYMENT_PLUGIN_ORIGIN_KEY,
  RESPONSE_STATUS
} from "utils/constants";

import {
  makeSelectOrder,
  makeSelectPaymentForm,
  makeSelectOrderSummaryPayload,
  makeSelectIsFetchingData,
  makeSelectUserDetails,
  makeSelectCurrencyCode,
  makeSelectGuestOrderOrderRef,
  makeSelectUserSIN,
  makeSelectSessionToken,
  makeSelectPaymentDetails,
  makeSelectProcessPayment,
  makeSelectCardTypesMap,
  makeSelectCardTypes,
  makeSelectPaymentErrorCodes
} from "ducks/selectors";
import {
  postPaymentRequest,
  fetchUpfrontPaymentDetails,
  saveAndGetPaymentCardDetails,
  processCardUpdatePayment,
  processUpfrontPayment,
  fetchPaymentStatus,
  clearData
} from "ducks/actions";
import { getOMSToken } from "ducks/actions/uiActions";
import SmartRender from "containers/SmartRender";
import {
  loadScript,
  getChannelFromUrl,
  gotoUrl,
  urlParam
} from "utils/helpers";
import usePaymentResponseHandler from "hooks/usePaymentResponseHandler";
import { getTxnId } from "utils/localStorage";

const UPDATE_FROM = {
  MOBILE: "mobile",
  WEB: "web"
};

const PaymentHOC = Component =>
  function HOC(props) {
    const {
      isFetchingPaymentRequest,
      paymentsPayload,
      userSIN,
      currencyCode,
      orderRef,
      change,
      clearPaymentDetails,
      locale,
      errorCodes
    } = props;
    const [isValidCardDetails, setIsValidCardDetails] = useState(false);
    const dispatch = useDispatch();
    const isPaymentExecuted = useSelector(
      ({ ui }) => ui?.isGetTokenExecuted || false
    );

    // on mount
    useEffect(() => {
      clearPaymentDetails();
      dispatch(getOMSToken(false));
      loadScript(
        "GMO_TOKEN_SCRIPT",
        process.env.REACT_APP_GMO_TOKEN_URL,
        () => {
          if (window.Multipayment) {
            window.Multipayment.init(process.env.REACT_APP_GMO_SHOP_ID);
          }
        }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCardDetailsPayload = (token, cardDetails) => {
      const creditCardAdditionalData = getCreditCardAdditionalData(cardDetails);
      return {
        service_instance_number: userSIN,
        additional_data: {
          custom_payment_method: "card",
          token: token,
          ...creditCardAdditionalData
        },
        paymentMethod: "card",
        currency: currencyCode,
        txn_id: orderRef,
        uuid: orderRef
      };
    };

    const getUpdateCardPayload = (token, cardDetails) => {
      const creditCardAdditionalData = getCreditCardAdditionalData(cardDetails);
      return {
        service_instance_number: userSIN,
        additional_data: {
          custom_payment_method: "card",
          token: token,
          ...creditCardAdditionalData
        },
        paymentMethod: "card",
        currency: currencyCode,
        txn_id: urlParam("txn_id") || orderRef,
        uuid: urlParam("txn_id") || orderRef
      };
    };

    const getCreditCardAdditionalData = ({ cardNumber, cardType }) => {
      if (cardNumber && cardNumber.length > 10) {
        return {
          first_six_digits: cardNumber.substring(0, 6),
          last_four_digits: cardNumber.substring(cardNumber.length - 4),
          card_type: cardType?.value
        };
      }
    };

    const dispatchAction = action => {
      dispatch(
        action.request(
          action.payload,
          (result, error) => {
            if (result.status !== RESPONSE_STATUS.FAILURE && !error) {
              gotoUrl(action.redirect, false, {}, true);
            } else {
              // Get the error code from payment service api response.
              const errorCode = get(result, "error_code");

              /*
               * Check if the error code matches with ccm payment service
               * GMO related error codes.
               */
              if (errorCodes[errorCode]) {
                Alert.error(get(locale, errorCodes[errorCode]));
              } else {
                Alert.error(
                  get(
                    locale,
                    "saving_or_updating_payment_error",
                    "There was an error saving/updating the payment method. Please try again."
                  )
                );
              }
            }
          },
          "v3"
        )
      );
    };

    const onGetToken = cardDetails => {
      if (window.Multipayment && cardDetails.isValid) {
        window.Multipayment.getToken(
          {
            cardno: cardDetails.cardNumber,
            expire: cardDetails.expiryDate,
            securitycode: cardDetails.code,
            tokennumber: 2
          },
          result => {
            dispatch(getOMSToken(false));
            const token = result?.tokenObject?.token;

            if (Array.isArray(token) && !isEmpty(token)) {
              const deepLink = urlParam("deepLink") || urlParam("return_url");
              // when opening update-card from webfront we need to pass update_from url param to identify the request is coming from
              // external or within webfront.
              const updateFrom =
                urlParam("update_from") || (deepLink ? "mobile" : false);
              let payment_method = paymentsPayload.payment_methods;
              payment_method.additional_data = getCreditCardAdditionalData(
                cardDetails
              );
              const paymentActions = {
                save: {
                  request: postPaymentRequest,
                  payload: {
                    ...paymentsPayload,
                    token
                  },
                  redirect: paths.PAYMENT_STATUS.replace(
                    ":channel",
                    getChannelFromUrl()
                  )
                },
                update: {
                  request: processCardUpdatePayment,
                  payload: getUpdateCardPayload(token, cardDetails),
                  redirect:
                    deepLink ||
                    paths.DASHBOARD.replace(":channel", getChannelFromUrl())
                }
              };

              // if update card page opened from webfront then register transaction and proceed with the card update flow
              if (updateFrom === UPDATE_FROM.WEB) {
                dispatch(
                  saveAndGetPaymentCardDetails(
                    {
                      ...getCardDetailsPayload(token, cardDetails)
                    },
                    (_, error) => {
                      if (!error) {
                        dispatchAction(paymentActions.update);
                      } else {
                        Alert.error(
                          get(
                            locale,
                            "invalid_card_details",
                            "There was an error getting your card details."
                          )
                        );
                      }
                    },
                    "v3"
                  )
                );

                // if url has auth_token url param thats mean we opened the update card page in mobile.
              } else if (updateFrom === UPDATE_FROM.MOBILE) {
                dispatchAction(paymentActions.update);

                // save card payment information
              } else {
                dispatchAction(paymentActions.save);
              }

              // error to show when there is a problem generating the token from GMO side
            } else {
              Alert.error(
                get(
                  locale,
                  "invalid_card_details",
                  "There was an error with the payment. Please try again."
                )
              );
            }
          }
        );
      } else {
        dispatch(getOMSToken(false));
      }
    };

    const onPayment = (_, payload, data) => {
      change("payment_method", get(data, "next.payment_method", "card"));
      dispatch(getOMSToken(true));
    };

    const onCardUpdate = () => {
      dispatch(getOMSToken(true));
    };

    const onChange = cardDetails => {
      if (cardDetails) {
        const { isValid = false } = cardDetails;

        if (isValidCardDetails !== isValid) {
          setIsValidCardDetails(isValid);
        }
      }
    };

    return isFetchingPaymentRequest ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <Component
        isPaymentExecuted={isPaymentExecuted}
        onGetToken={onGetToken}
        onCardPayment={onPayment}
        onCardUpdate={onCardUpdate}
        onChange={onChange}
        isValidCardDetails={isValidCardDetails}
        {...props}
      />
    );
  };

const mapStateToProps = createStructuredSelector({
  originkey: () => CIRCLES_PAYMENT_PLUGIN_ORIGIN_KEY,
  token: () => urlParam("token"),
  returnUrl: () => urlParam("return_url") || urlParam("deepLink"),
  order: makeSelectOrder(),
  paymentsForm: makeSelectPaymentForm(),
  paymentsPayload: makeSelectOrderSummaryPayload(),
  userDetails: makeSelectUserDetails(),
  userSIN: makeSelectUserSIN(),
  currencyCode: makeSelectCurrencyCode(),
  orderRef: makeSelectGuestOrderOrderRef(),

  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  isFetchingPaymentDetails: makeSelectIsFetchingData("paymentDetails"),
  paymentDetails: makeSelectPaymentDetails(),
  processPaymentData: makeSelectProcessPayment(),

  orderSessionToken: makeSelectSessionToken(),
  webTxnId: () => getTxnId(),
  cardMap: makeSelectCardTypesMap(),
  cardTypes: makeSelectCardTypes(),
  errorCodes: makeSelectPaymentErrorCodes()
});

export function mapDispatchToProps(dispatch) {
  return {
    onSubmit: (payload, paymentMode) => {
      const payloadWithPaymentMethod = {
        ...payload,
        payment_methods: {
          code: paymentMode.code,
          channel_code: paymentMode.channel_code
        }
      };

      if (!isEmpty(paymentMode)) {
        dispatch(
          postPaymentRequest(
            payloadWithPaymentMethod,
            usePaymentResponseHandler
          )
        );
      }
    },
    saveAndGetPaymentCardDetails: (params, callback, version) => {
      dispatch(saveAndGetPaymentCardDetails(params, callback, version));
    },
    fetchUpfrontPaymentDetails: (params, callback) => {
      dispatch(fetchUpfrontPaymentDetails(params, callback));
    },
    processCardUpdatePayment: (params, callback) => {
      dispatch(processCardUpdatePayment(params, callback));
    },
    processUpfrontPayment: (params, callback) => {
      dispatch(processUpfrontPayment(params, callback));
    },
    fetchPaymentStatus: (params, callback) => {
      dispatch(fetchPaymentStatus(params, callback));
    },
    clearPaymentDetails: () => {
      dispatch(clearData("paymentDetails"));
    }
  };
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  verifyPaymentHiddenFormSubmission: () => {
    const actionObject = get(
      stateProps,
      "processPaymentData.action_object",
      {}
    );
    const { url, method, data } = actionObject;

    if (url && !isEmpty(data)) {
      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", url);

      forEach(data, (value, key) => {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", value);
        form.appendChild(hiddenField);
      });

      document.body.appendChild(form);
      form.submit();
    }
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.PAYMENT_FORM,
    destroyOnUnmount: false
  }),
  memo
)(PaymentHOC(SmartRender));
